import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Document, Page } from 'react-pdf';
import { pdfjs } from 'react-pdf';
import { DndContext, useDraggable } from '@dnd-kit/core';
import { FileText, ArrowLeft, Save, Loader2, CheckCircle2, Move, RefreshCw, AlertTriangle, Plus, Trash2, X, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Font mapping for signature display
const getSignatureFontStyle = (fontName) => {
  switch (fontName) {
    case 'Dancing Script':
      return "'Dancing Script', cursive";
    case 'Pinyon Script':
      return "'Pinyon Script', cursive";
    default:
      return 'Caveat, cursive';
  }
};

// Draggable Field Component for Admin
function AdminDraggableField({ field, scale, onDelete, canDelete }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: field.id,
  });

  const style = {
    left: `${field.x * scale}px`,
    top: `${field.y * scale}px`,
    width: `${field.width * scale}px`,
    minHeight: `${field.height * scale}px`,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    cursor: isDragging ? 'grabbing' : 'grab',
    zIndex: isDragging ? 1000 : 10,
  };

  const isSigned = field.signed;

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`absolute rounded-lg p-2 transition-shadow border-2 ${
        isSigned 
          ? 'bg-green-50/90 border-green-400' 
          : 'bg-orange-50/90 border-orange-400 border-dashed'
      } ${isDragging ? 'shadow-xl ring-2 ring-blue-500' : 'shadow-md hover:shadow-lg hover:ring-2 hover:ring-blue-300'}`}
      style={style}
    >
      <div className="flex flex-col items-center justify-center">
        {isSigned ? (
          <>
            {field.signature_type === 'drawn' && field.signature_data ? (
              <img 
                src={field.signature_data} 
                alt="Unterschrift" 
                className="max-w-full object-contain pointer-events-none"
                style={{ 
                  maxHeight: `${field.height * scale * 0.7}px`,
                  minHeight: '30px'
                }}
              />
            ) : field.signature_type === 'typed' && field.signature_data ? (
              <span 
                className="text-xl font-medium text-zinc-800 pointer-events-none"
                style={{ fontFamily: getSignatureFontStyle(field.signature_font) }}
              >
                {field.signature_data}
              </span>
            ) : (
              <CheckCircle2 className="h-6 w-6 text-green-600 pointer-events-none" />
            )}
            {field.signer_name && (
              <span className="text-xs text-zinc-700 mt-1 font-medium pointer-events-none">{field.signer_name}</span>
            )}
          </>
        ) : (
          <span className="text-sm text-orange-600 font-medium pointer-events-none">Unterschriftsfeld</span>
        )}
      </div>
      <div className={`absolute -top-2 -right-2 text-white rounded-full p-1 shadow ${isSigned ? 'bg-green-500' : 'bg-orange-500'}`}>
        <Move className="h-3 w-3" />
      </div>
      {/* Delete button - only for unsigned fields */}
      {canDelete && !isSigned && (
        <button
          className="absolute -top-2 -left-2 bg-red-500 text-white rounded-full p-1 shadow hover:bg-red-600 z-50"
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onDelete(field.id);
          }}
          title="Feld löschen"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

export default function AdminEditDocument() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [signatureFields, setSignatureFields] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [pageWidth, setPageWidth] = useState(800);
  const [pdfError, setPdfError] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [credentials, setCredentials] = useState(null);

  useEffect(() => {
    // Check admin credentials
    const savedCreds = localStorage.getItem('admin_credentials');
    if (!savedCreds) {
      toast.error('Bitte melden Sie sich zuerst an');
      navigate('/admin');
      return;
    }
    setCredentials(JSON.parse(savedCreds));
    
    updatePageWidth();
    window.addEventListener('resize', updatePageWidth);
    return () => window.removeEventListener('resize', updatePageWidth);
  }, [navigate]);

  useEffect(() => {
    if (credentials) {
      loadDocument();
    }
  }, [credentials, documentId]);

  useEffect(() => {
    if (document) {
      const timestamp = Date.now();
      setPdfUrl(`${API}/document/${documentId}/pdf?t=${timestamp}`);
      setSignatureFields(document.signature_fields || []);
      setPdfError(false);
    }
  }, [document, documentId]);

  const updatePageWidth = () => {
    const maxWidth = Math.min(window.innerWidth - 400, 800);
    setPageWidth(maxWidth);
  };

  const loadDocument = async () => {
    try {
      const response = await axios.get(
        `${API}/admin/document/${documentId}/details`,
        { headers: { 'Authorization': `Basic ${credentials.auth}` } }
      );
      setDocument(response.data);
      setSignatureFields(response.data.signature_fields || []);
      setLoading(false);
    } catch (error) {
      toast.error('Dokument konnte nicht geladen werden');
      setLoading(false);
      navigate('/admin');
    }
  };

  const handleDragEnd = (event) => {
    const { active, delta } = event;
    if (!delta) return;

    const scale = pageWidth / 595;
    const fieldId = active.id;

    setSignatureFields(prev => prev.map(field => {
      if (field.id === fieldId) {
        return {
          ...field,
          x: Math.max(0, field.x + delta.x / scale),
          y: Math.max(0, field.y + delta.y / scale)
        };
      }
      return field;
    }));
    setHasChanges(true);
  };

  const addNewField = (pageIndex) => {
    const newField = {
      id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      x: 100,
      y: 100,
      width: 200,
      height: 60,
      page: pageIndex,
      signed: false,
      signature_data: null,
      signature_type: null,
      signer_email: null,
      signer_name: null,
      signer_role: null,
      signed_at: null
    };
    setSignatureFields(prev => [...prev, newField]);
    setHasChanges(true);
    toast.success(`Neues Feld auf Seite ${pageIndex + 1} hinzugefügt`);
  };

  const deleteField = (fieldId) => {
    const field = signatureFields.find(f => f.id === fieldId);
    if (field?.signed) {
      toast.error('Unterschriebene Felder können nicht gelöscht werden');
      return;
    }
    setSignatureFields(prev => prev.filter(f => f.id !== fieldId));
    setHasChanges(true);
    toast.success('Feld gelöscht');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/document/${documentId}/fields`,
        { signature_fields: signatureFields },
        { headers: { 'Authorization': `Basic ${credentials.auth}` } }
      );
      setHasChanges(false);
      toast.success('Änderungen gespeichert!');
    } catch (error) {
      toast.error('Fehler beim Speichern');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const downloadCurrentPdf = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(
        `${API}/document/${documentId}/download-current`,
        { 
          headers: { 'Authorization': `Basic ${credentials.auth}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'dokument_aktuell.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF heruntergeladen');
    } catch (error) {
      toast.error('Fehler beim Herunterladen');
      console.error(error);
    } finally {
      setDownloading(false);
    }
  };

  const handlePdfLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setPdfError(false);
  }, []);

  const handlePdfLoadError = useCallback((error) => {
    console.error('PDF load error:', error);
    setPdfError(true);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-zinc-600" />
          <p className="text-zinc-600">Dokument wird geladen...</p>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Card className="p-8 max-w-md text-center">
          <h2 className="text-2xl font-bold mb-4">Dokument nicht gefunden</h2>
          <Button onClick={() => navigate('/admin')}>Zurück zum Admin</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin')}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Zurück
              </Button>
              <div className="h-6 w-px bg-zinc-200"></div>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-zinc-950 rounded-md flex items-center justify-center">
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    Felder bearbeiten
                  </h1>
                  <p className="text-sm text-zinc-500">{document.original_filename}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {hasChanges && (
                <span className="text-sm text-amber-600 font-medium">Ungespeicherte Änderungen</span>
              )}
              <Button
                variant="outline"
                onClick={downloadCurrentPdf}
                disabled={downloading}
                className="gap-2"
              >
                {downloading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                PDF Download
              </Button>
              <Button
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className="gap-2 bg-zinc-950 text-white hover:bg-zinc-800"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Speichern
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <div className="flex gap-6">
          {/* PDF Viewer */}
          <div className="flex-1">
            <DndContext onDragEnd={handleDragEnd}>
              <div className="pdf-container bg-white shadow-2xl shadow-zinc-900/10 rounded-sm">
                {pdfError ? (
                  <div className="flex flex-col items-center justify-center p-12 min-h-[400px]">
                    <AlertTriangle className="h-16 w-16 text-orange-500 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">PDF konnte nicht geladen werden</h3>
                    <Button
                      onClick={() => setPdfUrl(`${API}/document/${documentId}/pdf?t=${Date.now()}`)}
                      variant="outline"
                      className="gap-2"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Neu laden
                    </Button>
                  </div>
                ) : (
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={handlePdfLoadSuccess}
                    onLoadError={handlePdfLoadError}
                    loading={
                      <div className="flex items-center justify-center p-12 min-h-[400px]">
                        <Loader2 className="h-8 w-8 animate-spin text-zinc-600" />
                      </div>
                    }
                  >
                    {Array.from(new Array(numPages), (el, index) => (
                      <div key={`page_${index + 1}`} className="relative mb-4">
                        <Page
                          pageNumber={index + 1}
                          width={pageWidth}
                          renderTextLayer={true}
                          renderAnnotationLayer={true}
                        />
                        {/* Render signature fields for this page */}
                        {signatureFields
                          .filter(field => field.page === index)
                          .map(field => {
                            const scale = pageWidth / 595;
                            return (
                              <AdminDraggableField
                                key={field.id}
                                field={field}
                                scale={scale}
                                onDelete={deleteField}
                                canDelete={true}
                              />
                            );
                          })}
                        {/* Add Field button for this page */}
                        <button
                          className="absolute top-2 right-2 bg-blue-500 text-white rounded-full p-2 shadow-lg hover:bg-blue-600 transition-colors z-30"
                          onClick={() => addNewField(index)}
                          title={`Neues Feld auf Seite ${index + 1} hinzufügen`}
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </Document>
                )}
              </div>
            </DndContext>
          </div>

          {/* Sidebar */}
          <div className="w-80">
            <Card className="p-6 sticky top-24">
              <h3 className="font-semibold text-lg mb-4">
                Unterschriftsfelder ({signatureFields.length})
              </h3>
              
              <div className="space-y-3 mb-6">
                {signatureFields.map((field, index) => (
                  <div 
                    key={field.id}
                    className={`p-3 rounded-lg border ${field.signed ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">Feld {index + 1}</span>
                      <div className="flex items-center gap-2">
                        {field.signed ? (
                          <span className="text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded">Unterschrieben</span>
                        ) : (
                          <>
                            <span className="text-xs text-orange-700 bg-orange-100 px-2 py-0.5 rounded">Ausstehend</span>
                            <button
                              onClick={() => deleteField(field.id)}
                              className="text-red-500 hover:text-red-700 p-1"
                              title="Feld löschen"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {field.signer_name && (
                      <p className="text-sm text-zinc-600 mt-1">{field.signer_name}</p>
                    )}
                    <p className="text-xs text-zinc-400 mt-1">
                      Seite {field.page + 1} • X: {Math.round(field.x)}, Y: {Math.round(field.y)}
                    </p>
                  </div>
                ))}
                {signatureFields.length === 0 && (
                  <p className="text-sm text-zinc-500 text-center py-4">
                    Keine Felder vorhanden. Klicken Sie auf das + Symbol im PDF, um ein neues Feld hinzuzufügen.
                  </p>
                )}
              </div>

              <div className="pt-4 border-t border-zinc-200">
                <h4 className="font-medium text-sm mb-2">Hinweise:</h4>
                <ul className="text-xs text-zinc-600 space-y-1">
                  <li>• Ziehen Sie die Felder, um sie zu verschieben</li>
                  <li>• Klicken Sie auf das + Symbol, um neue Felder hinzuzufügen</li>
                  <li>• Klicken Sie auf das X Symbol, um ununterschriebene Felder zu löschen</li>
                  <li>• Unterschriebene Felder können nicht gelöscht werden</li>
                  <li>• Klicken Sie auf &quot;Speichern&quot;, um die Änderungen zu übernehmen</li>
                </ul>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
